import { FC } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ParseResult } from 'papaparse';
import { useCSVReader } from 'react-papaparse';
import { useGetAllPriceListsQuery } from '@services';
import { CsvRow } from '../StateBasedPricing/StateBasedPricing';

type Props = {
  addInvalidHeader: (header: string) => void;
  addValidationError: (error: string) => void;
  clearState: () => void;
  setCsvRows: (rows: CsvRow[]) => void;
};

const StateBasedPricingCsvReader: FC<Props> = ({ addInvalidHeader, addValidationError, clearState, setCsvRows }) => {
  const { data: priceLists } = useGetAllPriceListsQuery();
  const stateNames = priceLists?.map((priceList) => priceList.name.toLowerCase()) || [];
  const validHeaders = ['sku', 'name', 'price', ...stateNames];
  const { CSVReader } = useCSVReader();

  const validateHeaders = (headers: string[]) => {
    headers.forEach((header) => {
      const headerIsValid = validHeaders.includes(header);

      if (!headerIsValid) {
        addInvalidHeader(header);
      }
    });
  };

  const convertCsvToRows = (csv: ParseResult<string[]>) => {
    const headers: string[] = csv.data[0];

    validateHeaders(headers);

    const rows = csv.data.slice(1);

    // Convert rows to objects that can more easily be used to create the correct data types.
    const csvRows: CsvRow[] = rows.map((row) => {
      return headers.reduce((csvRow: CsvRow, header: string, i: number) => {
        const newRow = {
          ...csvRow,
          [header]: row[i],
        };

        if (stateNames.includes(header)) {
          const valueAsNumber = Number(newRow[header]);

          isNaN(valueAsNumber) && addValidationError(`Value for ${header} must be a number.`);
        }

        return newRow;
      }, {});
    });

    setCsvRows(csvRows);
  };

  const handleOnFileLoad = (data: ParseResult<string[]>) => {
    clearState();
    convertCsvToRows(data);
  };

  return (
    <CSVReader onUploadAccepted={(results: any) => handleOnFileLoad(results)}>
      {({ getRootProps, acceptedFile }: any) => (
        <Box minWidth={400}>
          <Typography variant="subtitle1" mb={1} data-testid="state-pricing-reader-file-name-text">
            {(acceptedFile && acceptedFile?.name) || 'No file selected'}
          </Typography>
          <Button variant="outlined" component="span" {...getRootProps()} data-testid="state-pricing-reader-choose-file-btn">
            {acceptedFile ? 'Choose another file' : 'Choose file'}
          </Button>
        </Box>
      )}
    </CSVReader>
  );
};

export default StateBasedPricingCsvReader;
